import { Button } from '../../components/ui/button';
import { BookOpen, Bookmark, BookmarkCheck, FileText } from 'lucide-react';

const TABS = [
  { key: 'content', icon: BookOpen, label: 'Content' },
  { key: 'bookmarks', icon: Bookmark, label: 'Bookmarks' },
  { key: 'notes', icon: FileText, label: 'Notes' },
];

function ContentTab({ current }) {
  if (current?.type !== 'slide') return null;
  return (
    <div className="space-y-3">
      <h3 className="font-medium">{current.slide.title || 'Slide Content'}</h3>
      {current.slide.content?.text && <p className="text-muted-foreground">{current.slide.content.text}</p>}
      {current.slide.content?.bullet_points?.map((bp, bpi) => (
        <p key={`panel-bp-${bpi}`} className="text-muted-foreground">&bull; {bp}</p>
      ))}
      {current.slide.content?.speaker_notes && (
        <div className="mt-4 p-3 rounded-lg bg-muted">
          <p className="text-xs font-medium mb-1">Speaker Notes</p>
          <p className="text-xs text-muted-foreground">{current.slide.content.speaker_notes}</p>
        </div>
      )}
    </div>
  );
}

function BookmarksTab({ progress, allSlides, onJumpTo }) {
  const bookmarks = progress?.bookmarks || [];
  if (!bookmarks.length) return <p className="text-muted-foreground text-center py-8">No bookmarks yet</p>;
  return (
    <div className="space-y-2">
      {bookmarks.map((b) => (
        <button
          key={b.slide_id}
          onClick={() => {
            const idx = allSlides.findIndex((s) => s.type === 'slide' && s.slide?.slide_id === b.slide_id);
            if (idx >= 0) onJumpTo(idx);
          }}
          className="w-full text-left p-2 rounded-lg hover:bg-muted flex items-center gap-2"
        >
          <BookmarkCheck className="w-4 h-4 text-amber-500 shrink-0" />
          <span className="truncate">Slide: {b.slide_id?.slice(0, 8)}</span>
        </button>
      ))}
    </div>
  );
}

function NotesTab({ noteText, onNoteChange, onNoteBlur, onSaveNote, progress }) {
  return (
    <div className="space-y-3">
      <textarea
        value={noteText}
        onChange={onNoteChange}
        onBlur={onNoteBlur}
        placeholder="Add notes for this slide..."
        className="w-full h-32 p-3 rounded-lg border bg-background text-sm resize-none"
        data-testid="note-input"
      />
      <Button size="sm" onClick={onSaveNote} className="w-full">Save Note</Button>
      {(progress?.notes || []).length > 0 && (
        <div className="border-t pt-3 space-y-2">
          <p className="text-xs font-medium text-muted-foreground">All Notes</p>
          {progress.notes.map((n) => (
            <div key={n.note_id || n.slide_id} className="p-2 rounded-lg bg-muted text-xs">{n.content}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PlayerSidePanel({
  panelTab, onTabChange, current, progress, allSlides, onJumpTo,
  noteText, onNoteChange, onSaveNote,
}) {
  return (
    <div className="w-[360px] border-l bg-card flex flex-col shrink-0" data-testid="side-panel">
      <div className="flex border-b">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => onTabChange(t.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-medium ${
              panelTab === t.key ? 'border-b-2 border-[var(--lumyn-content)] text-[var(--lumyn-content)]' : 'text-muted-foreground'
            }`}
          >
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-4 text-sm">
        {panelTab === 'content' && <ContentTab current={current} />}
        {panelTab === 'bookmarks' && (
          <BookmarksTab progress={progress} allSlides={allSlides} onJumpTo={onJumpTo} />
        )}
        {panelTab === 'notes' && (
          <NotesTab
            noteText={noteText}
            onNoteChange={onNoteChange}
            onNoteBlur={onSaveNote}
            onSaveNote={onSaveNote}
            progress={progress}
          />
        )}
      </div>
    </div>
  );
}
